import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Masonry from "@mui/lab/Masonry";
import { Alert, CardContent, CardHeader, OutlinedInput, Paper, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import { createForm } from "rc-form";
import * as React from "react";
import { InventoryIcon } from "../../components/InventoryIcon";
import { Config } from "../../config/config";
import { getGlobalVar, Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface FeedbackStates {
    loaded: boolean;
    inventory: any[];
    amountFormVisible: boolean;
    activeLevelForm: any;
}

interface FeedbackProps {
    playerId: string;
    activeEnv: any;
    playerData: any;
    heroesConfig: any;
    form: any;
    classes: any;
}

const categories = [{
    id: "keys",
    label: "Resources",
}, {
    id: "boosters",
    label: "Speed ups",
}, {
    id: "cash_packs",
    label: "Cash packs",
}, {
    id: "vip_items",
    label: "VIP items",
}, {
    id: "chest",
    label: "Briefcases",
}, {
    id: "elixir_packs",
    label: "Elixir packs",
}, {
    id: "power_packs",
    label: "Power packs",
}, {
    id: "gold_packs",
    label: "Gold packs",
}, {
    id: "gear_packs",
    label: "Gear Packs",
}, {
    id: "gold_coin_packs",
    label: "Gold Coin Packs",
}];

class PlayerInventoryMain extends React.Component<FeedbackProps, FeedbackStates> {

    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            inventory: [],
            amountFormVisible: false,
            activeLevelForm: null,
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    fetchInventory = () => axios.get(`${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/inventory`, {
        headers: {Authorization: Helpers.getSessionId(), env: this.props.activeEnv},
    })

    fetchData = () =>
        axios.all([this.fetchInventory()]).then(axios.spread((
            inventory,
        ) => {
            this.setState({
                loaded: true,
                inventory: inventory.data.data,
            });
        })).catch((_err) => {
            PopupMessage.error("Something went wrong");
        })

    refreshList = () => {
        this.setState({ loaded: false }, () => {
            this.fetchData();
        });
    }

    getInventoryItemName = (item) => {
        const itemObject = getGlobalVar("ITEMS").find(i => i.id === item.itemId);
        return itemObject ? itemObject.name : `[UNDEFINED ITEM (ID=${item.itemId})]`;
    }

    excludeItems = (type) => {
        switch (type) {
            case "keys":
                return [90, 91, 92, 2, 3, 5, 6, 7, 8, 10, 11, 15, 16, 93, 17, 14];
            case "chest":
                return [
                    "8r79duojvouvzi4",
                    "4g2gjvs7ldfk",
                    "4g2gjvtbypah",
                    "4g2gjvtbzqyi",
                    "4g2gjvtc0zab",
                    "4g2gjvtc1ilv",
                    "3n4kayhki8zqijw",
                    "5601kq0k7lwe1sf",
                    "56011quk6bv9yhv",
                    "b4t2rchkazc0hgj",
                    "56015xzk67xt4k1",
                    "56019rxk56i6sau",
                    "56019rxk56hvmsi",
                    "56019rxk56i9gu6",
                    "56015xzk6815ip2",
                    "9287johkmbnf9is",
                    "4kbfjyecohnn",
                    "j63vhkihle1yh",
                    "cdhx8ikc63djyi",
                    "5601b9lk7luql1y",
                    "5601kq0k7lwdjjz"
                ];
            default:
                return [];
        }
    }

    categoryByType(type) {
        return getGlobalVar("ITEMS").reduce((items, item) => {
            if (item.type === type && this.excludeItems(type).indexOf(item.id) === -1) {
                const existingItem = this.state.inventory.find(itemObject => itemObject.itemId === item.id);

                items.push({
                    itemId: item.id,
                    itemType: item.type,
                    amount: existingItem?.amount || 0,
                });
            }

            return items;
        }, []).sort((a, b) => +a.itemId - +b.itemId);
    }

    closeForm = () => this.setState({
        amountFormVisible: false,
        activeLevelForm: null,
    })

    openForm = (itemIndex) => this.setState({
        amountFormVisible: true,
        activeLevelForm: itemIndex,
    })

    handleLevelChange = () => {
        this.props.form.validateFields((err, values) => {
            if (!err) {
                if (!this.state.activeLevelForm) {
                    return;
                }

                const itemObject = getGlobalVar("ITEMS").find(i => i.id === this.state.activeLevelForm);
                const itemType = itemObject ? itemObject.type : this.state.activeLevelForm;

                axios.put(`${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/inventory`, {
                    ...values,
                    amount: +values.amount,
                    id: this.state.activeLevelForm,
                    type: itemType,
                }, {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                        env: this.props.activeEnv,
                    },
                })
                    .then(data => {
                        const tempInventory = [...this.state.inventory];
                        const followerItem = tempInventory.find(item => item.itemId.toString() === this.state.activeLevelForm.toString());

                        if (!followerItem) {
                            tempInventory.push({
                                itemId: this.state.activeLevelForm,
                                amount: +values.amount,
                                itemType,
                            });
                        } else {
                            followerItem.amount = +values.amount;
                        }

                        return this.setState({
                            inventory: tempInventory,
                            amountFormVisible: false,
                            activeLevelForm: null,
                        });
                    })
                    .catch(_error => {
                        PopupMessage.error("Something went wrong!");
                    });
            }
        });
    }

    _handleKeyPress = (e) => {
        if (e.key === "Enter") {
            this.handleLevelChange();
        }
    }

    giveWeaponsAndGear = async (_) => {
        await axios.put(`${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/give_gear`, {
            test: "abc",
        }).then(
            () => {
                PopupMessage.success("User inventory updated");
            },
            (error) => {
                PopupMessage.error(error.response.data.message);
            },
        );
    }

    levelForm(data) {
        const {getFieldDecorator} = this.props.form;
        const formItemStyle = {
            display: "inline-block",
        };

        const formButtonStyle = {
            display: "inline-block",
            fontSize: 20,
            marginLeft: 5,
        };

        const successButtonStyle = {
            ...formButtonStyle,
            color: "green",
        };

        const cancelButtonStyle = {
            ...formButtonStyle,
        };

        return <React.Fragment>
            <div style={formItemStyle}>
                {getFieldDecorator("amount", {
                    initialValue: parseInt(data.amount, 10),
                })(
                    <OutlinedInput
                        autoFocus
                        onKeyDown={this._handleKeyPress}
                        style={{ width: 80 }}
                        size="small"
                    />,
                )}
            </div>
            <div style={{position: "absolute", left: 100, top: 15}}>
                <div style={successButtonStyle}>
                    <CheckCircleOutlineIcon onClick={() => this.handleLevelChange()}/>
                </div>
                <div style={cancelButtonStyle}>
                    <CancelIcon onClick={() => this.closeForm()}/>
                </div>
            </div>
        </React.Fragment>;
    }

    toggleLevelInlineForm(data) {
        if (this.props.playerData.is_online) {
            return parseInt(data.amount, 10);
        }

        if (this.state.amountFormVisible && data.itemId === this.state.activeLevelForm) {
            return this.levelForm(data);
        }

        return <span onClick={() => this.openForm(data.itemId)} className="edit--field-unactive">
            {parseInt(data.amount, 10)}
        </span>;
    };

    renderTableRows(category) {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={3}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>
        }

        const data = this.categoryByType(category.id);

        if (data.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={3}>
                    <Alert severity="info">No Items found for this category</Alert>
                </TableCell>
            </TableRow>
        }

        return data.map(inventoryItem => (
            <TableRow key={inventoryItem.itemId}>
                <TableCell>{inventoryItem.itemId}</TableCell>
                <TableCell>
                    <div style={{ marginBottom: 1, display: "flex", alignItems: "center" }}>
                        {InventoryIcon({
                            itemId: inventoryItem.itemId,
                            style: { marginRight: 10 },
                        })}
                        {this.getInventoryItemName(inventoryItem)}
                    </div>
                </TableCell>
                <TableCell style={{ position: "relative" }}>
                    {this.toggleLevelInlineForm(inventoryItem)}
                </TableCell>
            </TableRow>
        ))
    }

    renderCardContentByIndex(index) {
        const category = categories[index];

        return <Paper className={this.props.classes.paper} elevation={0}>
            <CardHeader title={category.label}/>

            <CardContent>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Item Id</TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell>Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.renderTableRows(category)}
                    </TableBody>
                </Table>
            </CardContent>
        </Paper>;
    }

    render() {
        return (
            <Masonry columns={2} spacing={3}>
                {this.renderCardContentByIndex(0)}
                {this.renderCardContentByIndex(4)}
                {this.renderCardContentByIndex(2)}
                {this.renderCardContentByIndex(5)}
                {this.renderCardContentByIndex(1)}
                {this.renderCardContentByIndex(3)}
                {this.renderCardContentByIndex(6)}
                {this.renderCardContentByIndex(7)}
                {this.renderCardContentByIndex(8)}
                {this.renderCardContentByIndex(9)}
            </Masonry>
        );
    }
}

const styles: any = theme => ({
    paper: {
        padding: 16,
    },
    paperHeading: {
        marginTop: 0,
    },
});

export const PlayerInventory = withStyles(styles)(createForm()(PlayerInventoryMain));
